import React, { MouseEvent, MutableRefObject } from "react";
import { Button, SpinningLoader } from "risa-oasis-ui_v2";
import { FilterSection, FilterValues } from "../../data-model/filterValues";
import RefreshButton from "../refreshButton/refreshButton";
import SearchBar from "../searchBar/searchBar";
import TableHeader from "./tableHeader";

interface TableActionsProps {
  onSearchClicked: (searchText: string) => void;
  onRefreshButton?: () => void;
  tableName: string;
  defaultValueRef?: MutableRefObject<string | null>;
  showFilterButton?: boolean;
  filterSectionsData?: FilterSection[];
  onFilterApplyClicked?: (data: FilterValues) => void;
  hideSearchBar?: boolean;
  showExportButton?: boolean;
  showRefreshButton?: boolean;
  onExportButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  isDisabled?: boolean;
  onBulkAssignButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  showBulkAssignButton?: boolean;
  bulkAssignButtonDisabled?: boolean;
  onRefetchPAOrderButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  showRefetchPAOrderButton?: boolean;
  refetchPAOrderButtonDisabled?: boolean;
  isRefetchingPAOrder?: boolean;
  onSelectAll?: () => void;
  tableWidth: number;
  onSortTableData: (key: string, subKey?: string, type?: string) => void;
  sortedTableHeaders: any[];
  tableDataLength: number;
  exportingInProgress?: boolean;
  placeholder?: string;
  showOtherButton?: boolean;
  otherButtonMeta?: {
    text: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    disabled: boolean;
    buttonType: "primary" | "secondary" | "tertiary";
    size: "small" | "medium" | "large";
  };
  isCheckboxSelected?: boolean;
  screenName?: string;
  shouldLogEvent?: boolean;
}

const TableActions: React.FC<TableActionsProps> = ({
  onSearchClicked,
  tableName,
  defaultValueRef,
  hideSearchBar,
  showExportButton,
  showRefreshButton,
  onExportButtonClick,
  onRefreshButton,
  isDisabled,
  onBulkAssignButtonClick,
  showBulkAssignButton,
  bulkAssignButtonDisabled,
  onRefetchPAOrderButtonClick,
  showRefetchPAOrderButton,
  refetchPAOrderButtonDisabled,
  isRefetchingPAOrder,
  tableWidth,
  onSortTableData,
  sortedTableHeaders,
  onSelectAll,
  tableDataLength,
  exportingInProgress,
  placeholder,
  showOtherButton,
  otherButtonMeta,
  isCheckboxSelected,
}) => {
  return (
    <>
      <div className="table-actions--container sticky top-0 z-50 flex flex-col bg-white pt-1">
        <div className="flex justify-between">
          <div className="table-actions--search-filter flex w-1/2 flex-col gap-1">
            <div className="flex">
              {!hideSearchBar && (
                <div className="mr-1 flex-1">
                  <SearchBar
                    placeHolder={placeholder ?? "Search by Patient Name, Member ID ..."}
                    onSearchClick={onSearchClicked}
                    defaultValue={defaultValueRef?.current ?? ""}
                    id={tableName}
                    isDisabled={isDisabled}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="table-actions--right-container flex gap-2">
            {showBulkAssignButton && (
              <Button
                buttonType="secondary"
                disabled={bulkAssignButtonDisabled ?? false}
                size="medium"
                onClick={onBulkAssignButtonClick ?? (() => {})}
              >
                Bulk Assign
              </Button>
            )}
            {showRefetchPAOrderButton && (
              <Button
                buttonType="secondary"
                disabled={refetchPAOrderButtonDisabled ?? false}
                size="medium"
                onClick={onRefetchPAOrderButtonClick ?? (() => {})}
              >
                {isRefetchingPAOrder ? "Refetching..." : "Refetch PA Order"}
              </Button>
            )}
            {showExportButton && (
              <Button
                buttonType="secondary"
                disabled={exportingInProgress ?? false}
                size="medium"
                onClick={onExportButtonClick ?? (() => {})}
              >
                {exportingInProgress ? (
                  <div className="flex items-center gap-2">
                    <SpinningLoader />
                    <span className="text-sm font-medium">Exporting...</span>
                  </div>
                ) : (
                  "Export"
                )}
              </Button>
            )}
            {showOtherButton && (
              <Button
                buttonType={otherButtonMeta?.buttonType ?? "secondary"}
                disabled={otherButtonMeta?.disabled ?? false}
                size={otherButtonMeta?.size ?? "medium"}
                onClick={otherButtonMeta?.onClick ?? (() => {})}
              >
                {otherButtonMeta?.text ?? "Other Button"}
              </Button>
            )}
            {showRefreshButton && <RefreshButton onClick={onRefreshButton} />}
          </div>
        </div>

        <div className="mt-2">
          <table key={tableName}>
            {tableDataLength > 0 && (
              <TableHeader
                sortedTableHeaders={sortedTableHeaders}
                tableWidth={tableWidth}
                onSortTableData={onSortTableData}
                onSelectAll={onSelectAll}
                isCheckboxSelected={isCheckboxSelected ?? false}
              />
            )}
          </table>
        </div>
      </div>
    </>
  );
};

export default TableActions;
